import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import {
  Box,
  Collapse,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { memo, useCallback, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { embeddedSidebarWidth } from '@/constants/embeddedApplication';
import type { EmbeddedNavigationGroup } from '@/types/embeddedApplication';
import { resolveEmbeddedIcon } from '@/utils/embeddedApplicationUtils';

interface EmbeddedNavigationProps {
  groups: EmbeddedNavigationGroup[];
  selectedItemId: string;
  expandedGroups: Record<string, boolean>;
  navigationQuery: string;
  onNavigationQueryChange: (value: string) => void;
  onToggleGroup: (groupId: string) => void;
  onSelectItem: (itemId: string) => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

interface NavigationPanelProps {
  groups: EmbeddedNavigationGroup[];
  selectedItemId: string;
  expandedGroups: Record<string, boolean>;
  navigationQuery: string;
  onNavigationQueryChange: (value: string) => void;
  onToggleGroup: (groupId: string) => void;
  onSelectItem: (itemId: string) => void;
}

const NavigationPanel = memo(function NavigationPanel({
  groups,
  selectedItemId,
  expandedGroups,
  navigationQuery,
  onNavigationQueryChange,
  onToggleGroup,
  onSelectItem,
}: NavigationPanelProps) {
  const { t } = useTranslation('entity-portal');

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onNavigationQueryChange(event.target.value);
    },
    [onNavigationQueryChange],
  );

  return (
    <Box
      sx={{
        width: embeddedSidebarWidth,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        borderRight: 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <TextField
          value={navigationQuery}
          onChange={handleSearchChange}
          placeholder={t('embedded.embeddedNavigationSearchPlaceholder')}
          size="small"
          fullWidth
          aria-label={t('embedded.embeddedNavigationSearchPlaceholder')}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', py: 1 }}>
        {groups.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ px: 2, py: 1 }}
          >
            {t('embedded.embeddedNavigationEmpty')}
          </Typography>
        ) : (
          groups.map((group) => {
            const shouldRenderSubheader =
              Boolean(group.label) || Boolean(group.collapsible);
            const isExpanded = group.collapsible
              ? (expandedGroups[group.id] ?? true)
              : true;

            return (
              <List
                key={group.id}
                dense
                subheader={
                  shouldRenderSubheader ? (
                    <ListSubheader
                      component="div"
                      sx={{
                        bgcolor: 'transparent',
                        py: 1,
                        px: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography
                        variant="navSection"
                        sx={{ color: 'text.secondary' }}
                      >
                        {group.label}
                      </Typography>
                      {group.collapsible ? (
                        <IconButton
                          size="small"
                          aria-label={
                            isExpanded
                              ? t('embedded.embeddedNavigationCollapseGroup', {
                                  group: group.label,
                                })
                              : t('embedded.embeddedNavigationExpandGroup', {
                                  group: group.label,
                                })
                          }
                          onClick={() => onToggleGroup(group.id)}
                        >
                          {isExpanded ? (
                            <ExpandLessIcon fontSize="small" />
                          ) : (
                            <ExpandMoreIcon fontSize="small" />
                          )}
                        </IconButton>
                      ) : null}
                    </ListSubheader>
                  ) : undefined
                }
              >
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  {group.items.map((item) => {
                    const ItemIcon = resolveEmbeddedIcon(item.icon);
                    const isSelected = item.id === selectedItemId;

                    return (
                      <ListItemButton
                        key={item.id}
                        selected={isSelected}
                        onClick={() => onSelectItem(item.id)}
                        aria-current={isSelected ? 'page' : undefined}
                        sx={{
                          mx: 1,
                          mb: 0.25,
                          borderRadius: 1.5,
                          '&.Mui-selected': {
                            bgcolor: 'action.selected',
                            '& .MuiListItemIcon-root': {
                              color: 'primary.main',
                            },
                            '& .MuiListItemText-primary': {
                              color: 'primary.main',
                              typography: 'subtitle1',
                            },
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <ItemIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            variant: 'body2',
                            noWrap: true,
                          }}
                        />
                      </ListItemButton>
                    );
                  })}
                </Collapse>
              </List>
            );
          })
        )}
      </Box>
    </Box>
  );
});

export const EmbeddedNavigation = memo(function EmbeddedNavigation({
  groups,
  selectedItemId,
  expandedGroups,
  navigationQuery,
  onNavigationQueryChange,
  onToggleGroup,
  onSelectItem,
  mobileOpen = false,
  onMobileClose,
}: EmbeddedNavigationProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const panel = (
    <NavigationPanel
      groups={groups}
      selectedItemId={selectedItemId}
      expandedGroups={expandedGroups}
      navigationQuery={navigationQuery}
      onNavigationQueryChange={onNavigationQueryChange}
      onToggleGroup={onToggleGroup}
      onSelectItem={onSelectItem}
    />
  );

  if (isMobile) {
    return (
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: embeddedSidebarWidth,
          },
        }}
      >
        {panel}
      </Drawer>
    );
  }

  return panel;
});
